import { CalendarDate } from '@internationalized/date';

/** Display dates as year-month-day (local calendar), consistent across the app. */
const formatLocalDateYmd = (date: Date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

/** Same YYYY-MM-DD layout as formatLocalDateYmd, for plain calendar dates (no time zone). */
export const formatCalendarDateYmd = (date: CalendarDate) => {
  const m = String(date.month).padStart(2, '0');
  const d = String(date.day).padStart(2, '0');
  return `${date.year}-${m}-${d}`;
};

export const formatDateString = (
  value: string | Date | null,
  options?: Intl.DateTimeFormatOptions
) => (value ? new Date(value).toLocaleDateString('en-GB', { timeZone: 'UTC', ...options }) : '');

/** Local calendar date from an ISO-like timestamp (same rules as formatIsoDate). For parsing, not display. */
export const calendarDateFromIsoInLocalTimezone = (value: string): CalendarDate | null => {
  if (!value) return null;

  let normalized = value;
  if (!/[zZ]|[+-]\d{2}:?\d{2}?$/.test(value)) {
    normalized = `${value}Z`;
  }

  const date = new Date(normalized);
  if (Number.isNaN(date.getTime())) return null;

  return new CalendarDate(date.getFullYear(), date.getMonth() + 1, date.getDate());
};

export const formatIsoDate = (
  value: string,
  removeTime: boolean = false,
  separator: string = ' '
) => {
  if (!value) return value;

  let normalized = value;

  if (!/[zZ]|[+-]\d{2}:?\d{2}?$/.test(value)) {
    normalized = `${value}Z`;
  }

  const date = new Date(normalized);

  const localDatePart = formatLocalDateYmd(date);
  const localHours = String(date.getHours()).padStart(2, '0');
  const localMinutes = String(date.getMinutes()).padStart(2, '0');
  const localSeconds = String(date.getSeconds()).padStart(2, '0');

  const formattedLocal = `${localDatePart}${separator}${localHours}:${localMinutes}:${localSeconds}`;
  if (removeTime) return localDatePart;

  const offsetMinutes = -date.getTimezoneOffset();
  const offsetHours = Math.floor(Math.abs(offsetMinutes) / 60);
  const sign = offsetMinutes >= 0 ? '+' : '-';
  const formattedOffset = `UTC${sign}${offsetHours.toString().padStart(2, '0')}`;

  return `${formattedLocal} (${formattedOffset})`;
};
